import { FocusEvent, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GetColorStatus, MaterialSymbol, OrganizationTotals, withStyles } from 'vopay';
import { BodyText, Button } from 'UI';
import usePartnerAccountStatusLabel from '@/components/Entities/hooks/usePartnerAccountStatusLabel';
import formatOrganizationBalance from './formatOrganizationBalance.helper';
import type { EntityCreation } from './entityCreationsFor.helper';
import type { OrganizationNode } from './organizationTree';
import styles from './EntityCard.module.scss';

const cn = withStyles(styles);

type EntityCardProps = {
  node: OrganizationNode;
  /** The "Account · 26 client accounts · 3 sub-accounts" line, built by the chart so the height estimate matches. */
  meta: string;
  /** Clicking the card opens the entity's details (the chart handles the click); false draws no pointer. */
  isClickable: boolean;
  /** The buttons the card shows on hover (spec 6.6), in order; none keeps the balance on hover. */
  creations: EntityCreation[];
  onCreate: (creation: EntityCreation, node: OrganizationNode) => void;
  /** Set for an entity in Application Pending: Track Progress takes the balance's place (spec 6.8). */
  onTrackProgress?: (node: OrganizationNode) => void;
  onToggleChildren: (node: OrganizationNode) => void;
  /** Set on the organization's own card, which has no balance of its own: the book's total, as the summary card reads it. */
  totalBalances?: OrganizationTotals['Balances'];
};

/**
 * One entity of the organization chart (spec 6.5): status dot, name, type and child counts, the
 * available balance when it has one, and the chip that expands and collapses its branch. Rendered in
 * its own React root by the chart engine, outside the app's providers, so everything comes in as props.
 */
export default function EntityCard({
  node,
  meta,
  isClickable,
  creations,
  onCreate,
  onTrackProgress,
  onToggleChildren,
  totalBalances,
}: EntityCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Entities.EntityCard' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();

  // Hovered or holding keyboard focus: the create buttons are up. Two states, so the mouse leaving
  // cannot hide a button that still holds focus; state rather than `:hover`, so the mouse and the
  // keyboard share one rule and a synthetic hover reaches it.
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isActive = isHovered || isFocused;

  const { entity, status, isFaded, isMatch, isExpanded, numChildren } = node;

  // Spec 6.5: the badge's colours, except that Disabled reads red beside Blocked (and Rejected, which
  // GetColorStatus already reads as negative).
  const tone =
    status === null ? 'none' : status === 'Blocked' || status === 'Disabled' ? 'negative' : GetColorStatus(status);

  const hasCreations = creations.length > 0;
  const isTrackable = status === 'Application Pending' && onTrackProgress !== undefined;
  // Spec 6.8: an entity that is not yet active has no balance, so a figure the row still carries
  // (a Blocked account keeps its funds) is only drawn once the account has been activated.
  const balance = status === 'Active' || status === 'Blocked' ? entity.AvailableBalance : null;

  // One line, every currency the book holds; an empty book draws no block.
  const totalFigures = Object.entries(totalBalances ?? {}).map(([currency, amount]) =>
    formatOrganizationBalance(amount, currency),
  );

  const balanceBlock =
    totalBalances !== undefined
      ? totalFigures.length > 0 && {
          label: t('totalBalance', { defaultValue: 'Total Balance' }),
          figure: totalFigures.join(' · '),
        }
      : balance !== null && {
          label: t('availableBalance', { defaultValue: 'Available Balance' }),
          figure: formatOrganizationBalance(balance, entity.Currency),
        };

  // The engine opens the entity on a click anywhere in the node; the card's own buttons stop there.
  function handleToggle(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onToggleChildren(node);
  }

  function handleCreate(event: MouseEvent<HTMLButtonElement>, creation: EntityCreation) {
    event.stopPropagation();
    onCreate(creation, node);
  }

  function handleTrackProgress(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onTrackProgress?.(node);
  }

  // Focus moving between the card's own buttons keeps it active; leaving the card drops it.
  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) setIsFocused(false);
  }

  const creationLabels: Record<EntityCreation, string> = {
    partner: t('addPartner', { defaultValue: 'Partner' }),
    account: t('addAccount', { defaultValue: 'Account' }),
    subAccount: t('addSubAccount', { defaultValue: 'Sub-Account' }),
  };

  return (
    <div
      className={cn(
        'card',
        isFaded && 'faded',
        isMatch && 'match',
        isClickable && 'clickable',
        hasCreations && isActive && 'active',
      )}
      data-testid={`entityCard-${node.id}`}
      data-entity-kind={node.kind}
      data-match={isMatch || undefined}
      data-faded={isFaded || undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
    >
      <div className={cn('content')}>
        {/* The status as the badge reads it: its dot and its label, in the badge's colour. */}
        <div className={cn('status', `tone-${tone}`)} data-testid="entityCardStatus" data-tone={tone}>
          <span className={cn('statusDot')} aria-hidden />
          {status ? resolveStatusLabel(status) : t('noStatus', { defaultValue: 'No status' })}
        </div>

        {/* Figma: Inter Semi Bold 14 — no vopay text component covers 14px, so the style lives in the module. */}
        <div className={cn('name')} title={entity.Name} data-testid="entityCardName">
          {entity.Name}
        </div>

        {/* Figma `Caption/10/Regular`, Text/Secondary75 — same gap. */}
        <div className={cn('meta')} data-testid="entityCardMeta">
          {meta}
        </div>

        {/* Spec 6.8: an entity still onboarding has no balance, so Track Progress takes its place. */}
        {isTrackable ? (
          <Button
            size="small"
            className={cn('trackProgress')}
            data-testid="entityCardTrackProgress"
            onClick={handleTrackProgress}
          >
            {t('trackProgress', { defaultValue: 'Track Progress' })}
          </Button>
        ) : (
          balanceBlock && (
            <div className={cn('balance')} title={balanceBlock.figure} data-testid="entityCardBalance">
              <span className={cn('balanceLabel')}>{balanceBlock.label}</span>

              {/* Body/13/Emphasized */}
              <BodyText size="sm" weight="em" className={cn('balanceFigure')}>
                {balanceBlock.figure}
              </BodyText>
            </div>
          )
        )}
      </div>

      {/* Figma `Button Group`: over the lower half of the card, in on hover and keyboard focus (6.6). The
          buttons stay in the tab order (and the accessibility tree) while hidden, so the keyboard can
          reach them to reveal them. */}
      {hasCreations && (
        <div className={cn('creations')} data-testid="entityCardCreations">
          {creations.map((creation) => (
            <Button
              key={creation}
              size="small"
              iconLeft="add"
              className={cn('creation')}
              data-testid={`entityCardCreate-${creation}`}
              onClick={(event) => handleCreate(event, creation)}
            >
              {creationLabels[creation]}
            </Button>
          ))}
        </div>
      )}

      {numChildren > 0 && (
        <button
          type="button"
          className={cn('chip')}
          aria-expanded={isExpanded}
          aria-label={t('toggleChildren', { defaultValue: 'Entities under {{name}}', name: entity.Name })}
          data-testid="entityCardChip"
          onClick={handleToggle}
        >
          <span data-testid="entityCardChipCount">{numChildren}</span>
          <MaterialSymbol icon={isExpanded ? 'expand_less' : 'expand_more'} size="16px" />
        </button>
      )}
    </div>
  );
}
